import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/hooks/useNotify';
import {
  useUpdateDraftInvoice,
  type InvoiceDetail,
} from '@/hooks/useInvoices';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import {
  formatMissingFieldLabels,
  isStaleInvoiceVersionError,
  translateBillingError,
} from '@/lib/billingErrors';
import { normalizePaymentTermsDaysOptions } from '@/features/settings/workshopSettingsPayload';
import {
  dueDateIsoFromPaymentTermsDays,
  pickDefaultPaymentTermsDays,
} from '@/components/workorders/billing/paymentSplit';
import { summarizeEditorLines, type InvoiceEditorLine } from './invoiceEditorLines';
import {
  buildUpdateDraftPayload,
  defaultDueDateInput,
  draftEditorValidationKey,
  dueDateIsoToDateInput,
  editorLinesFromInvoice,
  missingDraftFinalizationFields,
  resolveDraftPaymentTermsDays,
  sellerIbanFromSettings,
} from './draftInvoiceForm';

export function useDraftInvoiceEditor(
  invoice: InvoiceDetail | undefined,
  refetch: () => Promise<unknown>,
) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateDraft = useUpdateDraftInvoice();
  const { tenantSettings } = useTenantSettings();

  const paymentTermsOptions = useMemo(
    () => normalizePaymentTermsDaysOptions(tenantSettings?.paymentTermsDaysOptions),
    [tenantSettings?.paymentTermsDaysOptions],
  );

  const [lines, setLinesState] = useState<InvoiceEditorLine[]>([]);
  const [dueDate, setDueDateState] = useState('');
  const [paymentTermsDays, setPaymentTermsDaysState] = useState(() =>
    pickDefaultPaymentTermsDays(paymentTermsOptions),
  );
  const [customerReference, setCustomerReferenceState] = useState('');
  const [paymentReferenceNumber, setPaymentReferenceNumberState] = useState('');
  const [notes, setNotesState] = useState('');
  const [termsAndConditions, setTermsState] = useState('');
  const [isDirty, setDirty] = useState(false);
  const [staleConflict, setStaleConflict] = useState(false);

  const invoiceKey = invoice ? `${invoice.id}:${invoice.version}:${invoice.updatedAt}` : '';

  useEffect(() => {
    if (!invoice || invoice.status !== 'draft') return;
    const termsDays = resolveDraftPaymentTermsDays(invoice, paymentTermsOptions);
    const dueDateInput = dueDateIsoToDateInput(invoice.dueDate)
      || defaultDueDateInput(invoice.issueDate, termsDays);
    setLinesState(editorLinesFromInvoice(invoice));
    setDueDateState(dueDateInput);
    setPaymentTermsDaysState(termsDays);
    setCustomerReferenceState(invoice.customerReference ?? '');
    setPaymentReferenceNumberState(invoice.paymentReferenceNumber ?? '');
    setNotesState(invoice.notes ?? '');
    setTermsState(invoice.termsAndConditions ?? '');
    setDirty(dueDateIsoToDateInput(invoice.dueDate) === '' && dueDateInput !== '');
  }, [invoice, invoiceKey, paymentTermsOptions]);

  const markDirty = useCallback(() => setDirty(true), []);

  const setLines = useCallback((next: InvoiceEditorLine[]) => {
    setLinesState(next);
    markDirty();
  }, [markDirty]);

  const setDueDate = useCallback((value: string) => {
    setDueDateState(value);
    markDirty();
  }, [markDirty]);

  const setPaymentTermsDays = useCallback((days: number) => {
    setPaymentTermsDaysState(days);
    if (invoice) {
      setDueDateState(
        dueDateIsoToDateInput(dueDateIsoFromPaymentTermsDays(days, new Date(invoice.issueDate))),
      );
    }
    markDirty();
  }, [invoice, markDirty]);

  const setCustomerReference = useCallback((value: string) => {
    setCustomerReferenceState(value);
    markDirty();
  }, [markDirty]);

  const setPaymentReferenceNumber = useCallback((value: string) => {
    setPaymentReferenceNumberState(value);
    markDirty();
  }, [markDirty]);

  const setNotes = useCallback((value: string) => {
    setNotesState(value);
    markDirty();
  }, [markDirty]);

  const setTermsAndConditions = useCallback((value: string) => {
    setTermsState(value);
    markDirty();
  }, [markDirty]);

  const totals = useMemo(
    () => summarizeEditorLines(lines, invoice?.discountCents ?? 0),
    [lines, invoice?.discountCents],
  );

  const validationKey = draftEditorValidationKey(lines, dueDate);
  const sellerIban = sellerIbanFromSettings(
    tenantSettings == null ? undefined : { bankIban: tenantSettings['bankIban'] },
  );
  const missingFields = missingDraftFinalizationFields({
    recipientName: invoice?.customerName,
    recipientEmail: invoice?.customerEmail,
    sellerIban,
    totalCents: totals.totalCents,
    requireEmail: true,
  });

  const missingFieldsLabel = formatMissingFieldLabels(missingFields, t);
  const finalizeBlockedReason = (() => {
    if (staleConflict) return t('error.staleVersion');
    if (isDirty) return t('edit.saveBeforeFinalize');
    if (validationKey) return t(validationKey);
    if (missingFieldsLabel !== '') return t('edit.cannotFinalize', { fields: missingFieldsLabel });
    return null;
  })();

  const canFinalize = finalizeBlockedReason === null && !updateDraft.isPending;

  const reload = useCallback(async () => {
    setStaleConflict(false);
    await refetch();
  }, [refetch]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!invoice || invoice.status !== 'draft') return false;
    const invalid = draftEditorValidationKey(lines, dueDate);
    if (invalid) {
      notify.error(tc('error'), t(invalid));
      return false;
    }
    try {
      await updateDraft.mutateAsync(buildUpdateDraftPayload(invoice, lines, {
        dueDate,
        notes,
        termsAndConditions,
        customerReference,
        paymentReferenceNumber,
      }));
      notify.success(t('edit.success'));
      setDirty(false);
      setStaleConflict(false);
      return true;
    } catch (err) {
      if (isStaleInvoiceVersionError(err)) {
        setStaleConflict(true);
        notify.error(tc('error'), t('error.staleVersion'));
        await refetch();
        return false;
      }
      notify.error(tc('error'), translateBillingError(err, t));
      return false;
    }
  }, [
    invoice,
    lines,
    dueDate,
    notes,
    termsAndConditions,
    customerReference,
    paymentReferenceNumber,
    updateDraft,
    notify,
    t,
    tc,
    refetch,
  ]);

  return {
    lines,
    setLines,
    dueDate,
    setDueDate,
    paymentTermsDays,
    setPaymentTermsDays,
    paymentTermsOptions,
    customerReference,
    setCustomerReference,
    paymentReferenceNumber,
    setPaymentReferenceNumber,
    notes,
    setNotes,
    termsAndConditions,
    setTermsAndConditions,
    totals,
    missingFields,
    save,
    saving: updateDraft.isPending,
    isDirty,
    canFinalize,
    finalizeBlockedReason,
    staleConflict,
    reload,
  };
}

export type DraftInvoiceEditorModel = ReturnType<typeof useDraftInvoiceEditor>;
